// noinspection DuplicatedCode

import {EventFromTarget} from './@types';
import initRaceMap from './map';
import {Runner} from './models/runner';
import ApiProvider from './providers/api';
import FavoritesList from './providers/favorites-list';
import PassedWaypoints from './providers/passed-waypoints';
import initRunnersStore, {RunnersStore} from './store/runners';
import RunnersProvider, {RunnersDetailResponse} from './providers/runners';
import RenderProvider from './providers/render';
import RunnerCountdown from './providers/runner-countdown';
import RunnerStatistics from './providers/runner-statistics';

export default class Tracking {
    private runnersProvider: RunnersProvider;

    private renderProvider: RenderProvider;

    private runnerCountdown: RunnerCountdown;

    private runnerStatistics: RunnerStatistics;

    private passedWaypoints: PassedWaypoints;

    private favoritesList: FavoritesList;

    private favoriteRunners: RunnersStore;

    private apiProvider: ApiProvider;

    private run: string;

    private runners: Runner[];

    private currentRunnerDetail: string | null = null;

    private onDelete: (event: Event, deleteRunner: Runner) => void;

    constructor() {
        this.runnersProvider = RunnersProvider.getInstance();
        this.renderProvider = RenderProvider.getInstance();
        this.runnerCountdown = RunnerCountdown.getInstance();
        this.runnerStatistics = RunnerStatistics.getInstance();
        this.passedWaypoints = PassedWaypoints.getInstance();
        this.favoritesList = FavoritesList.getInstance();
        this.favoriteRunners = initRunnersStore();
    }

    async fetchRunnersDetail(year: number, startNumbers: Array<string>) {
        this.apiProvider = new ApiProvider();
        return this.apiProvider.getData<RunnersDetailResponse>(year, `runners/${startNumbers.join(',')}`);
    }

    async init() {
        await initRaceMap();

        const {
            favoriteRunnerInterfaces: defaultRunnerInterfaces,
            run: defaultRun,
        } = this.favoriteRunners.getState();

        if (defaultRun) {
            const radioOptions = Array.from(this.renderProvider.searchFilterRunOptions) as HTMLInputElement[];
            const radio: HTMLInputElement = radioOptions.find(({value}: HTMLInputElement) => value === defaultRun);
            if (radio) {
                radio.checked = true;
            }
        }

        this.renderProvider.searchFilterRunOptions.forEach((radio) => {
            radio.addEventListener('change', ({target}: EventFromTarget<HTMLInputElement>) => {
                this.favoriteRunners.setCurrentRun(target.value);
                this.updateFavoritesList(this.favoriteRunners.getState().favoriteRunners, target.value);
            });
        });

        if (defaultRunnerInterfaces) {
            const favoriteRunners = await this.runnersProvider.getRunners(defaultRunnerInterfaces);
            this.updateFavoritesList(favoriteRunners, defaultRun);
        }

        this.run = this.favoriteRunners.getState().run;
        this.runners = this.favoriteRunners.getState().favoriteRunners;
        this.currentRunnerDetail = this.favoriteRunners.getState().currentRunnerDetail;

        this.favoriteRunners.subscribe(({
            favoriteRunners,
            run,
            currentRunnerDetail,
        }) => {
            const runHasChanged = run !== this.run;
            const runnersHaveChanged = !Tracking.compareRunnersArrays(this.runners, favoriteRunners);
            const visibleRunnerDetailHasChanged = currentRunnerDetail !== this.currentRunnerDetail;

            if (favoriteRunners.length === 0) {
                this.runnersProvider.unsubscribe();
                this.runnerCountdown.unsubscribe();
                this.runnerStatistics.unsubscribe();
                this.passedWaypoints.unsubscribe();
                this.favoritesList.unsubscribe();
                return;
            }

            this.currentRunnerDetail = currentRunnerDetail;
            this.runners = favoriteRunners;
            this.run = run;

            if (runHasChanged || runnersHaveChanged || visibleRunnerDetailHasChanged) {
                this.updateFavoritesList(favoriteRunners, run);
            }

            if (visibleRunnerDetailHasChanged) {
                this.updateRunnerDetail(favoriteRunners.find(({startNumber}) => startNumber === currentRunnerDetail));
            }
        });
    }

    static compareRunnersArrays(arrayA: Runner[], arrayB: Runner[]) {
        const arrayToString = (array: Runner[]) => array.map(({startNumber}) => startNumber).toString();
        return arrayToString(arrayA) === arrayToString(arrayB);
    }

    static hasReachedLimit(favoriteRunners: Runner[]): boolean {
        return favoriteRunners.length >= 10;
    }

    updateFavoritesList(favoriteRunners: Runner[], run: string) {
        this.favoritesList.subscribe(
            favoriteRunners,
            run,
            (clickedRunner: Runner) => {
                this.favoriteRunners.setCurrentRunnerDetail(clickedRunner.startNumber);

                this.onDelete = (event: Event, deleteRunner: Runner) => {
                    const newRunnersArray = favoriteRunners.filter(
                        ({startNumber}) => startNumber !== deleteRunner.startNumber,
                    );
                    this.favoriteRunners.setFavoriteRunners(newRunnersArray);
                    this.updateFavoritesList(newRunnersArray, run);
                };

                this.renderProvider.renderRunnerDetail(
                    clickedRunner,
                    () => {
                        this.favoriteRunners.setCurrentRunnerDetail(null);
                    },
                    this.onDelete,
                );
            },
        );

        if (Tracking.hasReachedLimit(favoriteRunners)) {
            this.renderProvider.showWarningLimitReached();
        } else {
            this.renderProvider.hideWarningLimitReachedIfVisible();
        }

        this.runnersProvider.subscribe(favoriteRunners);
    }

    updateRunnerDetail(runner: Runner) {
        if (!runner) return;

        this.renderProvider.renderRunnerDetail(
            runner,
            () => {
                this.favoriteRunners.setCurrentRunnerDetail(null);
            },
            this.onDelete,
        );

        if (runner.hasFinished()) {
            this.renderProvider.showFinishScreen();
        } else {
            this.renderProvider.hideFinishScreen();
        }

        if (!runner.hasDetailedData()) return;

        this.runnerStatistics.subscribe(runner);
        this.runnerCountdown.subscribe(runner);
        this.passedWaypoints.subscribe(runner);
    }
}
